/**
 * OrganizationId Value Object
 *
 * Validated branded type for Organization identifiers.
 *
 * @module domains/personnel/domain/value-objects/OrganizationId
 */

import type { OrganizationId } from '@personnel/domain/types';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Creates a validated OrganizationId
 *
 * @param id - The UUID string to validate
 * @returns {OrganizationId} A validated OrganizationId branded type
 * @throws {Error} When ID is empty or not a valid UUID
 *
 * @example
 * const orgId = createOrganizationId('550e8400-e29b-41d4-a716-446655440000');
 */
export const createOrganizationId = (id: string): OrganizationId => {
  if (!id?.trim()) {
    throw new Error('Organization ID is required');
  }

  if (!UUID_REGEX.test(id)) {
    throw new Error('Organization ID must be a valid UUID');
  }

  return id as OrganizationId;
};

/**
 * Checks if a string is a valid Organization ID format without throwing
 *
 * @param id - The string to validate
 * @returns {boolean} True if valid UUID format
 */
export const isValidOrganizationId = (id: string): boolean => {
  return !!id?.trim() && UUID_REGEX.test(id);
};
